import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { Avatar, Badge, CircularProgress, Divider, Tooltip } from '@mui/material';
import {
  AccountCircleOutlined, AttachMoneyOutlined, ChevronLeft, ChevronRight,
  DvrOutlined, HelpOutlineOutlined, HomeOutlined, LanguageOutlined,
  Login, Logout, SettingsOutlined, TimerOutlined, WorkOutline,
} from '@mui/icons-material';
import logo from '../../../../Assets/images/AirswiftPrimaryLogoWhiteTextCopy.png';
import birdLogo from '../../../../Assets/images/Airswift Primary Logo (Gradient Bird Only).png'
import { getTandEPendingCounts } from '../../../../actions/timesheet';
import { handleLogin, handleLogout } from '../../../../utils/loginFunctions';
import { useWindowSize } from '../../../../utils/hooks';
import { maxMobileSize } from '../../../../utils/constants';
import LanguageSwitcher from '../../../languageSwitcher/LanguageSwitcher';

function getInitials(name = '') {
  return name.split(' ').slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('');
}

function NavItem({ name, path, icon, badge, badgeColor = 'primary', external, collapsed, active, index = 0 }) {
  const cls = [
    'relative flex items-center gap-3 w-full no-underline cursor-pointer group',
    'text-[14px] font-medium',
    'transition-all duration-200 ease-out',
    collapsed ? 'justify-center px-0 py-3' : 'px-4 py-2.5',
    active
      ? 'text-white bg-white/[0.12]'
      : 'text-white/60 hover:text-white hover:bg-white/[0.07] hover:translate-x-[2px]',
  ].join(' ');

  const iconEl = (
    <Badge
      badgeContent={badge || 0}
      color={badgeColor}
      max={99}
      invisible={!badge}
      sx={{ '& .MuiBadge-badge': { fontSize: '10px', minWidth: '17px', height: '17px', padding: '0 4px' } }}
    >
      <span className={[
        'flex items-center transition-transform duration-200',
        active ? 'opacity-100 scale-[1.05]' : 'opacity-60 group-hover:opacity-100 group-hover:scale-[1.1]',
      ].join(' ')}>
        {React.cloneElement(icon, { sx: { fontSize: 20 } })}
      </span>
    </Badge>
  );

  const content = (
    <>
      {active && (
        <span
          className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#0094B3] rounded-r-[3px]"
          style={{ animation: 'active-bar-in 0.2s cubic-bezier(0.4,0,0.2,1) both' }}
        />
      )}
      {iconEl}
      {!collapsed && (
        <span
          className="truncate flex-1 transition-all duration-200"
          style={collapsed ? { opacity: 0, width: 0 } : { opacity: 1 }}
        >
          {name}
        </span>
      )}
    </>
  );

  const inner = collapsed
    ? <Tooltip title={name} placement="right" arrow><span className="flex w-full justify-center">{content}</span></Tooltip>
    : content;

  const staggerStyle = { animationDelay: `${index * 40}ms` };

  return external
    ? <a href={path} target="_blank" rel="noopener noreferrer" className={`${cls} nav-item-enter`} style={staggerStyle}>{inner}</a>
    : <Link to={path} className={`${cls} nav-item-enter`} style={staggerStyle}>{inner}</Link>;
}

export default function Sidebar({ collapsed, onCollapse, showMenu = true }) {
  const { width } = useWindowSize();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const { t } = useTranslation();
  const [isBeingSignedIn, setIsBeingSignedIn] = useState(false);

  const accessTo = useSelector((s) => s.accessToParts);
  const pendingTandECounts = useSelector((s) => s.timesheet?.tandecounts);

  const account = accounts?.[0];
  const fullName = account?.idTokenClaims?.given_name && account?.idTokenClaims?.family_name
    ? `${account.idTokenClaims.given_name} ${account.idTokenClaims.family_name}`
    : account?.name ?? '';
  const givenName = account?.idTokenClaims?.given_name ?? fullName.split(' ')[0] ?? '';
  const jobTitle = account?.idTokenClaims?.jobTitle ?? '';
  console.log('Sidebar.jsx: account:', account);
  const company = account?.idTokenClaims?.company ?? '';
  const initials = getInitials(fullName);

  const timesheetBadge = pendingTandECounts?.PendingTimesheetCount || 0;
  const expenseBadge = pendingTandECounts?.PendingClaimsCount || 0;

  const profile = useSelector((state) => state?.user?.profile);

  const currentWork = profile?.WorkHistories
    .filter((job) => job.CurrentlyWorkThere)
    ?.sort((a, b) => new Date(b.StartDate) - new Date(a.StartDate))[0];

  const primaryItems = [
    { name: 'Home', path: isAuthenticated ? '/dashboard' : '/', icon: <HomeOutlined />, show: showMenu },
    { name: 'Jobs', path: isAuthenticated ? '/dashboard/jobs' : '/jobs', icon: <WorkOutline />, show: !accessTo.tAndEApprover && showMenu },
    { name: 'Onboarding', path: '/onboardingInteraction', icon: <DvrOutlined />, show: accessTo.onboarding && showMenu },
    { name: 'Time', path: '/dashboard/timesheets', icon: <TimerOutlined />, show: (accessTo.tAndEApprover || accessTo.tAndEContractor) && showMenu, badge: timesheetBadge, badgeColor: 'primary' },
    { name: 'Expense', path: '/dashboard/expenses', icon: <AttachMoneyOutlined />, show: (accessTo.tAndEApprover || accessTo.tAndEContractor) && showMenu, badge: expenseBadge, badgeColor: 'error' },
    { name: 'Profile', path: '/my-profile', icon: <AccountCircleOutlined />, show: !accessTo.tAndEApprover && isAuthenticated && showMenu },
  ].filter((i) => i.show);

  const utilityItems = [
    { name: 'Settings', path: '/dashboard/settings', icon: <SettingsOutlined />, show: !accessTo.tAndEApprover && isAuthenticated && showMenu },
    { name: 'Support', path: 'https://www.airswift.com/en/knowledge', icon: <HelpOutlineOutlined />, show: showMenu, external: true },
  ].filter((i) => i.show);

  useEffect(() => {
    if (accessTo.tAndEApprover) dispatch(getTandEPendingCounts());
  }, [accessTo]);

  const handleAuth = () => {
    setIsBeingSignedIn(true);
    isAuthenticated ? handleLogout(instance) : handleLogin(instance);
  };

  if (width < maxMobileSize) return null;
  const isActive = (path) => location.pathname === path;

  return (
    <>
      <style>{`
        @keyframes sidebar-mount {
          from { transform: translateX(-100%); opacity: 0.6; }
          to   { transform: translateX(0);     opacity: 1;   }
        }
        @keyframes nav-item-enter {
          from { opacity: 0; transform: translateX(-8px); }
          to   { opacity: 1; transform: translateX(0);    }
        }
        @keyframes active-bar-in {
          from { transform: scaleY(0); }
          to   { transform: scaleY(1); }
        }
        @keyframes user-block-in {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0);    }
        }
        .nav-item-enter {
          animation: nav-item-enter 0.25s cubic-bezier(0.4,0,0.2,1) both;
        }
        .user-block-in {
          animation: user-block-in 0.3s cubic-bezier(0.4,0,0.2,1) 0.1s both;
        }
        .sidebar-root {
          animation: sidebar-mount 0.3s cubic-bezier(0.4,0,0.2,1) both;
        }
      `}</style>

      <aside
        style={{
          width: collapsed ? 64 : 220,
          minWidth: collapsed ? 64 : 220,
          transition: 'width 0.2s ease-in-out, min-width 0.2s ease-in-out',
        }}
        className="sidebar-root fixed top-0 left-0 h-screen flex flex-col shrink-0 bg-[#022D41] overflow-hidden z-[200]"
      >
        <div className={`flex justify-center items-center min-h-[80px] shrink-0 border-b border-white/[0.08] ${collapsed ? 'px-0' : 'px-5'}`}>
          <Link to={isAuthenticated ? '/dashboard' : '/'}>
            <img src={collapsed ? birdLogo : logo} alt="Airswift" className={`w-auto object-contain ${collapsed ? 'h-8' : 'h-13'}`} />
          </Link>
        </div>

        {isAuthenticated && (
          <div className={`user-block-in flex items-center gap-3 py-3.5 shrink-0 border-b border-white/[0.08] ${collapsed ? 'justify-center px-0' : 'px-4'}`}>
            <Avatar sx={{ width: 36, height: 36, bgcolor: '#0094B3', fontSize: '13px', fontWeight: 700, flexShrink: 0 }}>
              {initials}
            </Avatar>
            {!collapsed && (
              <div className="overflow-hidden">
                <div className="text-[14px] font-semibold text-white truncate leading-tight">Hi, {givenName}</div>
                <div className="text-[12px] text-white/50 truncate leading-tight mt-0.5">
                  {currentWork?.Title || company || 'Portal User'}
                </div>
              </div>
            )}
          </div>
        )}

        <button
          onClick={() => onCollapse(!collapsed)}
          aria-label={collapsed ? 'Expand' : 'Collapse'}
          className={`flex items-center gap-2 w-full shrink-0 py-2 text-[12px] font-medium text-white/40 bg-transparent border-none border-b border-white/[0.08] cursor-pointer hover:text-white/70 transition-colors duration-150 ${collapsed ? 'justify-center px-0' : 'px-4'}`}
        >
          {collapsed
            ? <ChevronRight sx={{ fontSize: 16 }} />
            : <><ChevronLeft sx={{ fontSize: 16 }} /><span className="tracking-widest uppercase text-[10px]">Collapse</span></>}
        </button>

        <nav className="flex flex-col py-1.5 flex-1 overflow-y-auto overflow-x-hidden [scrollbar-width:thin] [&::-webkit-scrollbar]:hidden">
          {primaryItems.map((item, i) => (
            <NavItem key={item.name} {...item} index={i} collapsed={collapsed} active={isActive(item.path)} />
          ))}
        </nav>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

        <nav className="flex flex-col py-1.5 shrink-0">
          {utilityItems.map((item, i) => (
            <NavItem key={item.name} {...item} index={primaryItems.length + i} collapsed={collapsed} active={isActive(item.path)} />
          ))}

          {showMenu && (
            <div className={`flex items-center gap-3 py-2.5 text-white/60 ${collapsed ? 'justify-center px-0' : 'px-4'}`}>
              {collapsed
                ? <Tooltip title="Language" placement="right" arrow><span className="opacity-60"><LanguageOutlined sx={{ fontSize: 20 }} /></span></Tooltip>
                : <LanguageSwitcher collapsed={collapsed} />}
            </div>
          )}
        </nav>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

        {showMenu && (
          <button
            onClick={handleAuth}
            className={`flex items-center gap-3 w-full mb-12 my-1 py-2.5 text-[14px] font-medium text-white/60 bg-transparent border-none cursor-pointer hover:text-white hover:bg-white/[0.07] transition-colors duration-150 ${collapsed ? 'justify-center px-0' : 'px-4'}`}
          >
            <span className="opacity-70 flex items-center">
              {isBeingSignedIn ? <CircularProgress size="1rem" sx={{ color: '#0094B3' }} />
                : isAuthenticated ? <Logout sx={{ fontSize: 20 }} />
                  : <Login sx={{ fontSize: 20 }} />}
            </span>
            {!collapsed && <span>{isAuthenticated ? 'Sign Out' : 'Sign In'}</span>}
          </button>
        )}
      </aside>
    </>
  );
}